import type { ProjectConversation, ProjectConversationMessage } from "../types/projectConversations";
import type { SpecSection, SpecSource } from "../types/specs";

interface PromoteToSpecInput {
  projectId: string;
  messageId?: string;
  messageText?: string;
  targetCategory?: string;
  specSectionId?: string;
  target?: {
    title?: string | null;
    category?: string | null;
    kind?: string | null;
    summary?: string | null;
    details?: string | null;
    content?: string | null;
  };
}

interface ConversationResponse {
  ok?: boolean;
  conversation?: ProjectConversation;
  messages?: ProjectConversationMessage[];
  message?: ProjectConversationMessage;
  userMessage?: ProjectConversationMessage;
  section?: SpecSection;
  updatedSections?: SpecSection[];
  specSource?: SpecSource;
  error?: string;
}

export async function fetchConversation(
  projectId: string,
): Promise<{ conversation: ProjectConversation; messages: ProjectConversationMessage[] }> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${projectId}/conversation`);
  const payload = (await response.json()) as ConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to load conversation (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation) {
    throw new Error("No conversation was returned by the server");
  }

  return {
    conversation: payload.conversation,
    messages: payload.messages ?? [],
  };
}

export async function sendConversationMessage(
  projectId: string,
  content: string,
): Promise<{ conversation: ProjectConversation; message: ProjectConversationMessage; messages: ProjectConversationMessage[] }> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const trimmedContent = content.trim();

  if (!trimmedContent) {
    throw new Error("message content is required");
  }

  const response = await fetch(`/api/projects/${projectId}/conversation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content: trimmedContent }),
  });

  const payload = (await response.json()) as ConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to send conversation message (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation || !payload?.message) {
    throw new Error("Conversation message was not returned by the server");
  }

  return {
    conversation: payload.conversation,
    message: payload.message,
    messages: payload.messages ?? [],
  };
}

export async function promoteConversationMessageToSpec(
  projectId: string,
  input: PromoteToSpecInput,
): Promise<{ section: SpecSection; specSource?: SpecSource; updatedSections?: SpecSection[] }> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!input.projectId) {
    throw new Error("project id is required");
  }

  if (!input.messageId && !input.messageText?.trim()) {
    throw new Error("message id or text is required");
  }

  const response = await fetch(`/api/projects/${projectId}/spec-sections/from-conversation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...input, projectId }),
  });

  const payload = (await response.json()) as ConversationResponse;

  if (!response.ok || payload.ok === false || !payload.section) {
    throw new Error(payload.error ?? `Failed to promote conversation message (status ${response.status})`);
  }

  return { section: payload.section, specSource: payload.specSource, updatedSections: payload.updatedSections };
}
